import { createSelector } from 'reselect';
import { IGuest } from '@bridebook/models/source/models/Weddings/Guests.types';
import { IDropdownOption } from '@bridebook/ui';
import { IGuestlistCategory } from '@bridebook/web/lib/guestlist/types';
import { getCategoryName } from '@bridebook/web/lib/guestlist/utils';
import { InitialisedGuestListStorage } from '@bridebook/web/lib/storage-manager/basic/initialised-guest-list';
import { IApplicationState } from '@bridebook/web/lib/types';
import { getPartners } from '@bridebook/web/lib/weddings/selectors';

const getCategories = (state: IApplicationState) => state.weddings.profile.guests.categories;

export const getTables = (state: IApplicationState) => state.weddings.profile.guests.tables;

const isCordova = (state: IApplicationState) => state.app.device.isCordova;

const getModalGuest = (state: IApplicationState) => state.guestlist.guest;

export const guestsCount = (state: IApplicationState) => state.weddings.profile.guests.count;

export const getGuests = (state: IApplicationState) => state.guestlist.list;

const hasInitialised = () => InitialisedGuestListStorage.get();

/* ############################################################################
 *  MEMOIZED SELECTORS
 * ######################################################################### */

export const getSortedCategories = createSelector(
  [getCategories],
  ({ data, sort = [] }): Array<IGuestlistCategory> =>
    sort.map((id: string) => ({
      id,
      value: data[id],
    })),
);

export const getCategoriesDropdownOptions = createSelector(
  [getSortedCategories, getPartners],
  (categories, partners): Array<IDropdownOption> =>
    categories.map((category: IGuestlistCategory) => ({
      value: category.id,
      label: getCategoryName({ category, partners }),
    })),
);

export const getGuestTableName = createSelector(
  [getTables, getModalGuest],
  (tables, guest): string | undefined => {
    if (!tables || !guest.table) {
      return undefined;
    }
    return tables.data[guest.table];
  },
);

export const getHasCordovaContacts = createSelector([isCordova], (isCordova): boolean => isCordova);

export const isGuestListInitialised = createSelector(
  [guestsCount, hasInitialised],
  (guestsCount, hasInitialised) => guestsCount > 0 || hasInitialised,
);
export const getCurrentGuest = createSelector([getModalGuest], (guest: IGuest) => guest);

export const getAddressManual = (state: IApplicationState) => state.guestlist.addressManual;

export const getGuestlistLoaded = (state: IApplicationState) => state.guestlist.loaded;

export const getShowGuestlistFilters = (state: IApplicationState) =>
  state.guestlist.showGuestlistFilters;

export const getShowGuestlistSearch = (state: IApplicationState) => state.guestlist.showSearch;

export const getCategoryDetailsPopup = (state: IApplicationState) =>
  state.guestlist.categoryDetailsPopup;

export const getIsGuestRsvpUpdatePending = (state: IApplicationState) =>
  state.guestlist.isGuestRsvpUpdatePending;

export const getManageGuestsPopup = (state: IApplicationState) => state.guestlist.manageGuestsPopup;
export const isAddBulkGuestsPopupVisible = (state: IApplicationState) =>
  state.guestlist.addGuestsPopupVisible;

export const getAddGuestsFlowView = (state: IApplicationState) => state.guestlist.addGuestsFlowView;

export const getIsEditing = (state: IApplicationState) => state.guestlist.edit;

export const getGuestsMissingEmail = (state: IApplicationState) =>
  state.guestlist.guestsMissingEmail;

export const getShowImportGuestsModal = (state: IApplicationState) =>
  state.guestlist.showImportGuestsModal;

export const getGuestlistGuestsCount = (state: IApplicationState) => state.guestlist.list.length;
